// Light, dark, or the system's (the tokens read `data-theme` on <html>, packages/tokens). A
// per-browser preference: kept in localStorage, applied before the first paint (main.tsx).
import { useSyncExternalStore } from 'react';

export type Theme = 'auto' | 'light' | 'dark';
const KEY = 'pf.theme';
const EVENT = 'pf-theme';

export function savedTheme(): Theme {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' ? v : 'auto';
  } catch {
    return 'auto';
  }
}

export function applyTheme(theme: Theme) {
  const root = document.documentElement;
  if (theme === 'auto') delete root.dataset.theme;
  else root.dataset.theme = theme;
}

export function setTheme(theme: Theme) {
  try {
    if (theme === 'auto') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, theme);
  } catch {}
  applyTheme(theme);
  window.dispatchEvent(new Event(EVENT));
}

const subscribe = (on: () => void) => {
  window.addEventListener(EVENT, on);
  return () => window.removeEventListener(EVENT, on);
};

export const useTheme = () => useSyncExternalStore(subscribe, savedTheme);

/** Whether the page is dark now: forced, or the system's when on Auto. */
export function isDark(): boolean {
  const t = savedTheme();
  return t === 'dark' || (t === 'auto' && window.matchMedia('(prefers-color-scheme: dark)').matches);
}

/** Calls back when the theme or the system's appearance changes. */
export function onAppearance(on: () => void) {
  const media = window.matchMedia('(prefers-color-scheme: dark)');
  media.addEventListener('change', on);
  window.addEventListener(EVENT, on);
  return () => {
    media.removeEventListener('change', on);
    window.removeEventListener(EVENT, on);
  };
}
